import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  NativeSelect,
  Spinner,
  Switch,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  ImageIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  ShieldAlertIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import {
  type CreatePlaygroundNodeRequest,
  type PlaygroundNodeSummary,
  playgroundNodesApi,
  type UpdatePlaygroundNodeRequest,
} from '~/lib/api'
import { toaster } from '~/toaster'

export const Route = createFileRoute('/_app/admin/playground-nodes')({
  component: PlaygroundNodesPage,
})

function CreateNodeModal({
  onClose,
  onSuccess,
  categories,
}: {
  onClose: () => void
  onSuccess: () => void
  categories: string[]
}) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [backgroundColor, setBackgroundColor] = useState('#E5E7EB')
  const [externalLink, setExternalLink] = useState('')
  const [displayOrder, setDisplayOrder] = useState('0')

  const createMutation = useMutation({
    mutationFn: (data: CreatePlaygroundNodeRequest) => playgroundNodesApi.create(data),
    onSuccess: () => {
      toaster.create({
        title: 'Node created',
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to create node',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!slug || !name) {
      toaster.create({
        title: 'Missing required fields',
        description: 'Name and slug are required',
        type: 'error',
      })
      return
    }

    // Validate slug format
    if (!/^[a-z0-9-]+$/.test(slug)) {
      toaster.create({
        title: 'Invalid slug',
        description: 'Slug must contain only lowercase letters, numbers, and hyphens',
        type: 'error',
      })
      return
    }

    createMutation.mutate({
      name,
      slug,
      category: category || undefined,
      description: description || undefined,
      backgroundColor,
      externalLink: externalLink || undefined,
      displayOrder: parseInt(displayOrder) || 0,
    })
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={1000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="500px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Add New Node</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Name *
                  </Text>
                  <Input
                    placeholder="PostgreSQL"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Slug *
                  </Text>
                  <Input
                    placeholder="postgresql"
                    value={slug}
                    onChange={(e) =>
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                    }
                    required
                  />
                </Box>
              </HStack>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Category
                  </Text>
                  <Input
                    placeholder="Databases"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    list="categories"
                  />
                  <datalist id="categories">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                  <Text fontSize="xs" color="content.secondary" mt={1}>
                    Leave empty for root level
                  </Text>
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Display Order
                  </Text>
                  <Input
                    type="number"
                    placeholder="0"
                    value={displayOrder}
                    onChange={(e) => setDisplayOrder(e.target.value)}
                  />
                </Box>
              </HStack>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Background Color
                  </Text>
                  <HStack gap={2}>
                    <Input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      w="60px"
                      h="40px"
                      p={1}
                      cursor="pointer"
                    />
                    <Input
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      placeholder="#E5E7EB"
                      maxW="120px"
                    />
                    <Box
                      w="40px"
                      h="40px"
                      bg={backgroundColor}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="border"
                    />
                  </HStack>
                </Box>
              </HStack>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Description
                </Text>
                <Textarea
                  placeholder="Brief description of the node..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  External Link
                </Text>
                <Input
                  placeholder="https://example.com"
                  value={externalLink}
                  onChange={(e) => setExternalLink(e.target.value)}
                  type="url"
                />
                <Text fontSize="xs" color="content.secondary" mt={1}>
                  Optional URL to link to when node is clicked
                </Text>
              </Box>

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="blue" loading={createMutation.isPending}>
                  Create Node
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

function EditNodeModal({
  node,
  onClose,
  onSuccess,
  categories,
}: {
  node: PlaygroundNodeSummary
  onClose: () => void
  onSuccess: () => void
  categories: string[]
}) {
  const queryClient = useQueryClient()
  const logoInputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState(node.name)
  const [slug, setSlug] = useState(node.slug)
  const [category, setCategory] = useState(node.category || '')
  const [description, setDescription] = useState(node.description || '')
  const [backgroundColor, setBackgroundColor] = useState(node.backgroundColor || '#E5E7EB')
  const [externalLink, setExternalLink] = useState(node.externalLink || '')
  const [displayOrder, setDisplayOrder] = useState(node.displayOrder.toString())
  const [isActive, setIsActive] = useState(node.isActive)

  const updateMutation = useMutation({
    mutationFn: (data: UpdatePlaygroundNodeRequest) => playgroundNodesApi.update(node.id, data),
    onSuccess: () => {
      toaster.create({
        title: 'Node updated',
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update node',
        description: error.message,
        type: 'error',
      })
    },
  })

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => playgroundNodesApi.uploadLogo(node.id, file),
    onSuccess: () => {
      toaster.create({
        title: 'Logo uploaded',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['playground-nodes'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to upload logo',
        description: error.message,
        type: 'error',
      })
    },
  })

  const deleteLogoMutation = useMutation({
    mutationFn: () => playgroundNodesApi.deleteLogo(node.id),
    onSuccess: () => {
      toaster.create({
        title: 'Logo deleted',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['playground-nodes'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete logo',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate slug format
    if (slug && !/^[a-z0-9-]+$/.test(slug)) {
      toaster.create({
        title: 'Invalid slug',
        description: 'Slug must contain only lowercase letters, numbers, and hyphens',
        type: 'error',
      })
      return
    }

    // Build update request - only include changed fields
    const updateData: UpdatePlaygroundNodeRequest = {}

    if (name !== node.name) updateData.name = name
    if (slug !== node.slug) updateData.slug = slug
    if (category !== (node.category || '')) updateData.category = category || undefined
    if (description !== (node.description || '')) updateData.description = description || undefined
    if (backgroundColor !== node.backgroundColor) updateData.backgroundColor = backgroundColor
    // For externalLink, send empty string to clear, or the actual URL to set
    if (externalLink !== (node.externalLink || '')) updateData.externalLink = externalLink
    if (parseInt(displayOrder) !== node.displayOrder)
      updateData.displayOrder = parseInt(displayOrder)
    if (isActive !== node.isActive) updateData.isActive = isActive

    updateMutation.mutate(updateData)
  }

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadLogoMutation.mutate(file)
    }
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={1000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="500px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Edit Node: {node.name}</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              {/* Logo upload */}
              <Box>
                <Text fontWeight="medium" mb={2} fontSize="sm">
                  Logo
                </Text>
                <HStack gap={4}>
                  <Box
                    borderWidth="2px"
                    borderStyle="dashed"
                    borderRadius="lg"
                    p={4}
                    textAlign="center"
                    cursor="pointer"
                    onClick={() => logoInputRef.current?.click()}
                    _hover={{ borderColor: 'blue.500' }}
                    flex={1}
                  >
                    {node.hasLogo ? (
                      <Image
                        src={playgroundNodesApi.getLogoUrl(node.id)}
                        alt="Logo"
                        maxH="60px"
                        mx="auto"
                        objectFit="contain"
                      />
                    ) : (
                      <VStack gap={1}>
                        <ImageIcon size={24} />
                        <Text fontSize="sm" color="content.secondary">
                          Click to upload logo
                        </Text>
                      </VStack>
                    )}
                  </Box>
                  {node.hasLogo && (
                    <Button
                      size="sm"
                      colorPalette="red"
                      variant="ghost"
                      onClick={() => deleteLogoMutation.mutate()}
                      loading={deleteLogoMutation.isPending}
                      aria-label="Delete logo"
                    >
                      <TrashIcon size={14} />
                    </Button>
                  )}
                </HStack>
                <input
                  type="file"
                  ref={logoInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleLogoUpload}
                />
              </Box>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Name *
                  </Text>
                  <Input
                    placeholder="PostgreSQL"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Slug *
                  </Text>
                  <Input
                    placeholder="postgresql"
                    value={slug}
                    onChange={(e) =>
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                    }
                    required
                  />
                </Box>
              </HStack>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Category
                  </Text>
                  <Input
                    placeholder="Databases"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    list="edit-categories"
                  />
                  <datalist id="edit-categories">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Display Order
                  </Text>
                  <Input
                    type="number"
                    placeholder="0"
                    value={displayOrder}
                    onChange={(e) => setDisplayOrder(e.target.value)}
                  />
                </Box>
              </HStack>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Background Color
                  </Text>
                  <HStack gap={2}>
                    <Input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      w="60px"
                      h="40px"
                      p={1}
                      cursor="pointer"
                    />
                    <Input
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      placeholder="#E5E7EB"
                      maxW="120px"
                    />
                    <Box
                      w="40px"
                      h="40px"
                      bg={backgroundColor}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="border"
                    />
                  </HStack>
                </Box>
              </HStack>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Description
                </Text>
                <Textarea
                  placeholder="Brief description of the node..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  External Link
                </Text>
                <Input
                  placeholder="https://example.com"
                  value={externalLink}
                  onChange={(e) => setExternalLink(e.target.value)}
                  type="url"
                />
                <Text fontSize="xs" color="content.secondary" mt={1}>
                  Optional URL to link to when node is clicked
                </Text>
              </Box>

              <HStack gap={2}>
                <Switch.Root checked={isActive} onCheckedChange={(e) => setIsActive(e.checked)}>
                  <Switch.HiddenInput />
                  <Switch.Control />
                </Switch.Root>
                <Text fontSize="sm">Active</Text>
              </HStack>

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="blue" loading={updateMutation.isPending}>
                  Save Changes
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

function PlaygroundNodesPage() {
  const { hasMinRole } = useAuth()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingNode, setEditingNode] = useState<PlaygroundNodeSummary | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // Check if user has at least operator role
  const isOperatorOrAbove = hasMinRole('operator')

  // Redirect if not operator or above
  if (!isOperatorOrAbove) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="danger.subtle">
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="danger.fg">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>operator</strong> role or higher.
          </Text>
        </VStack>
      </Flex>
    )
  }

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['playground-nodes', 'categories'],
    queryFn: () => playgroundNodesApi.getCategories(),
  })

  const categories = categoriesData?.categories || []

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'playground-nodes',
      { search: searchQuery, category: categoryFilter, page: currentPage, pageSize },
    ],
    queryFn: () =>
      playgroundNodesApi.list({
        page: currentPage,
        pageSize,
        activeOnly: false,
        search: searchQuery || undefined,
        category: categoryFilter || undefined,
      }),
    retry: false,
  })

  // Reset to first page when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value)
    setCurrentPage(1)
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => playgroundNodesApi.delete(id),
    onSuccess: () => {
      toaster.create({
        title: 'Node deleted',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['playground-nodes'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete node',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleDelete = (node: PlaygroundNodeSummary) => {
    if (confirm(`Are you sure you want to delete "${node.name}"?`)) {
      deleteMutation.mutate(node.id)
    }
  }

  const nodes = data?.nodes || []

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Playground Node Management"
        subtitle="Manage node types for the architecture playground"
        actions={
          <>
            <Button size="sm" colorPalette="blue" onClick={() => setShowCreateModal(true)}>
              <PlusIcon size={16} />
              Add Node
            </Button>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
          </>
        }
      />

      <VStack align="stretch" gap={6}>
        {/* Filters */}
        <HStack gap={4}>
          <Box flex={1} maxW="300px">
            <HStack>
              <SearchIcon size={16} />
              <Input
                placeholder="Search nodes..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                size="sm"
              />
            </HStack>
          </Box>
          <Box>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={categoryFilter}
                onChange={(e) => handleCategoryChange(e.target.value)}
                placeholder="All Categories"
              >
                <option value="">All Categories</option>
                <option value="__uncategorized__">Uncategorized</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Box>
        </HStack>

        <Card.Root>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader w="60px">Logo</Table.ColumnHeader>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Category</Table.ColumnHeader>
                <Table.ColumnHeader w="60px">Color</Table.ColumnHeader>
                <Table.ColumnHeader w="80px">Order</Table.ColumnHeader>
                <Table.ColumnHeader w="80px">Status</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={7}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : nodes.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={7}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No nodes found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                nodes.map((node) => (
                  <Table.Row key={node.id}>
                    <Table.Cell>
                      {node.hasLogo ? (
                        <Image
                          src={playgroundNodesApi.getLogoUrl(node.id)}
                          alt={node.name}
                          w="40px"
                          h="40px"
                          objectFit="contain"
                          borderRadius="md"
                        />
                      ) : (
                        <Box
                          w="40px"
                          h="40px"
                          bg="bg.subtle"
                          borderRadius="md"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <ImageIcon size={20} color="var(--chakra-colors-gray-400)" />
                        </Box>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <VStack align="flex-start" gap={0}>
                        <Text fontWeight="medium">{node.name}</Text>
                        <Text fontSize="sm" color="content.secondary">
                          {node.slug}
                        </Text>
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" color={node.category ? 'inherit' : 'content.tertiary'}>
                        {node.category || 'Root level'}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Box
                        w="24px"
                        h="24px"
                        bg={node.backgroundColor}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="border"
                        title={node.backgroundColor}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{node.displayOrder}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette={node.isActive ? 'green' : 'red'} variant="subtle">
                        {node.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={1}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingNode(node)}
                          aria-label={`Edit ${node.name}`}
                        >
                          <PencilIcon size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => handleDelete(node)}
                          aria-label={`Delete ${node.name}`}
                        >
                          <TrashIcon size={14} />
                        </Button>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>

        {data && data.total > 0 && (
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="content.secondary">
              Showing {(currentPage - 1) * pageSize + 1} -{' '}
              {Math.min(currentPage * pageSize, data.total)} of {data.total} nodes
            </Text>
            <HStack gap={1}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeftIcon size={16} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeftIcon size={16} />
              </Button>
              <HStack gap={1} px={2}>
                {Array.from({ length: Math.ceil(data.total / pageSize) }, (_, i) => i + 1)
                  .filter((page) => {
                    const totalPages = Math.ceil(data.total / pageSize)
                    if (totalPages <= 7) return true
                    if (page === 1 || page === totalPages) return true
                    if (Math.abs(page - currentPage) <= 1) return true
                    return false
                  })
                  .reduce<(number | 'ellipsis')[]>((acc, page, idx, arr) => {
                    if (idx > 0 && page - (arr[idx - 1] as number) > 1) {
                      acc.push('ellipsis')
                    }
                    acc.push(page)
                    return acc
                  }, [])
                  .map((item, idx) =>
                    item === 'ellipsis' ? (
                      <Text key={`ellipsis-${idx}`} px={2} color="content.tertiary">
                        ...
                      </Text>
                    ) : (
                      <Button
                        key={item}
                        size="sm"
                        variant={currentPage === item ? 'solid' : 'outline'}
                        colorPalette={currentPage === item ? 'blue' : undefined}
                        onClick={() => setCurrentPage(item)}
                        minW="36px"
                      >
                        {item}
                      </Button>
                    ),
                  )}
              </HStack>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setCurrentPage((p) => Math.min(Math.ceil(data.total / pageSize), p + 1))
                }
                disabled={currentPage >= Math.ceil(data.total / pageSize)}
              >
                <ChevronRightIcon size={16} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(Math.ceil(data.total / pageSize))}
                disabled={currentPage >= Math.ceil(data.total / pageSize)}
              >
                <ChevronsRightIcon size={16} />
              </Button>
            </HStack>
          </Flex>
        )}
      </VStack>

      {showCreateModal && (
        <CreateNodeModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['playground-nodes'] })}
          categories={categories}
        />
      )}

      {editingNode && (
        <EditNodeModal
          node={editingNode}
          onClose={() => setEditingNode(null)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['playground-nodes'] })}
          categories={categories}
        />
      )}
    </Box>
  )
}
